import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot } from '@fortawesome/free-solid-svg-icons';
import { sortLineup, isPositionAllowed } from '../helpers/gameHelper';
import { subPlayer } from '../api/games';
import { toast } from 'react-toastify';
import PitcherFatigue, { getPitcherFatigueTier } from './PitcherFatigue';
import { getEffectivePitcherStamina } from '../helpers/cardHelpers';

const GameLineup = ({ participant, latestEvent, isSub = false, onClose, pitcherWorkMap }) => {
    const starters = participant.GameLineups.filter(gl => gl.fieldingPosition && !gl.gameEventIDExited);
    const bench = participant.GameLineups.filter(gl => !gl.fieldingPosition && !gl.gameEventIDExited);
    const used = participant.GameLineups.filter(gl => gl.gameEventIDExited);
    const [subInPlayer, setSubInPlayer] = useState(null);
    const [subOutPlayer, setSubOutPlayer] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const handleSubInClick = player => {
        setSubInPlayer(player);
        if (subOutPlayer && !isPositionAllowed(player.Card.positions, subOutPlayer.fieldingPosition)) {
            setSubOutPlayer(null);
        }
    };

    const handleSubOutClick = player => {
        setSubOutPlayer(player);
        if (subInPlayer && !isPositionAllowed(subInPlayer.Card.positions, player.fieldingPosition)) {
            setSubInPlayer(null);
        }
    };
    const submitSubstitution = async () => {
        if (submitting) return;
        setSubmitting(true);
        const subData = {
            gameID: participant.gameID,
            lineupOrder: subOutPlayer.lineupOrder,
            position: subOutPlayer.fieldingPosition,
            removedGameLineupID: subOutPlayer.gameLineupID,
            addedGameLineupID: subInPlayer.gameLineupID,
        };
        try {
            await subPlayer(subData);
            toast.success(`Player substituted successfully!`);
            onClose();
        } catch (error) {
            toast.error('Error substituting player: ' + error.message);
            console.error('Error substituting player:', error);
        } finally {
            setSubmitting(false);
        }
    };

    const renderLineup = (lineup, allowSubIn, allowSubOut, showPlayerPositions) => {
        //this makes my brain hurt
        const canSubIn = subOutPlayer ? isPositionAllowed(lineup.Card.positions, subOutPlayer.fieldingPosition) : true;
        const canSubOut = subInPlayer ? isPositionAllowed(subInPlayer.Card.positions, lineup.fieldingPosition) : true;

        // Show pitcher fatigue bar when pitcherWorkMap is provided and card is a pitcher
        const isPitcherCard = lineup.Card?.type === 'Pitcher';
        const showFatigueBar = pitcherWorkMap && isPitcherCard;
        let pitcherWorkValue = 0;
        if (showFatigueBar) {
            const inGameWork = pitcherWorkMap.get(String(lineup.gameLineupID));
            // If they've pitched in this game use that; otherwise show carry-in fatigue
            pitcherWorkValue = inGameWork ?? (lineup.startingFatigue ?? 0);
        }

        const isDisabled = (allowSubIn && !canSubIn) || (allowSubOut && !canSubOut);
        const isSubOut = subOutPlayer?.gameLineupID === lineup.gameLineupID;
        const isSubIn = subInPlayer?.gameLineupID === lineup.gameLineupID;

        return (
            <div
                key={lineup.gameLineupID}
                className={`player-row${
                    lineup.gameLineupID === latestEvent?.nextBatterLineup?.gameLineupID ||
                    lineup.gameLineupID === latestEvent?.nextPitcherLineup?.gameLineupID
                        ? ' highlight'
                        : ''
                }${isDisabled ? ' player-row-disabled' : ''}${isSubOut ? ' player-row-sub-out' : ''}${isSubIn ? ' player-row-sub-in' : ''}`}
            >
                <span className="player-name-line">
                    {lineup.lineupOrder !== null ? lineup.lineupOrder : ''}: {lineup.Card?.year} {lineup.Card?.name} (
                    {lineup.Card?.rating}){lineup.fieldingPosition ? ` - ${lineup.fieldingPosition} ` : ''}
                    {showPlayerPositions ? ` (${lineup.Card.positions}) ` : ''}
                    {showFatigueBar && (() => {
                        const stamina = getEffectivePitcherStamina(lineup.Card, lineup.CardInventory?.upgrades);
                        const baseStamina = lineup.Card?.stamina ?? 27;
                        return (
                            <span className="tooltip-container">
                                <PitcherFatigue
                                    pitcherWork={pitcherWorkValue}
                                    stamina={stamina}
                                />
                                <span className="tooltip">
                                    {getPitcherFatigueTier(pitcherWorkValue, stamina)}: {pitcherWorkValue}/{baseStamina}{stamina !== baseStamina ? ` (+${stamina - baseStamina})` : ''}
                                </span>
                            </span>
                        );
                    })()}
                </span>
                {isSub && allowSubIn && (
                    <button
                        type="button"
                        className={`sub-action-chip sub-in-chip${isSubIn ? ' selected' : ''}`}
                        aria-pressed={isSubIn}
                        onClick={() => handleSubInClick(lineup)}
                    >
                        Sub In
                    </button>
                )}
                {isSub && allowSubOut && (
                    <button
                        type="button"
                        className={`sub-action-chip sub-out-chip${isSubOut ? ' selected' : ''}`}
                        aria-pressed={isSubOut}
                        onClick={() => handleSubOutClick(lineup)}
                    >
                        Sub Out
                    </button>
                )}
                {isSub && (isSubIn || isSubOut) && subInPlayer && subOutPlayer && (
                    <button
                        type="button"
                        className="sub-action-chip sub-submit-chip"
                        onClick={submitSubstitution}
                        disabled={submitting}
                    >
                        Submit
                    </button>
                )}
            </div>
        );
    };

    return (
        <div className={`participant${isSub ? ' sub-player-list' : ''}`}>
            <h4>
                {participant.role} Team: {participant.UserTeam?.name}{' '}
                {!participant.userID && <FontAwesomeIcon icon={faRobot} />}
            </h4>

            {isSub ? (
                <>
                    <div className="sub-columns">
                        <div className="sub-col">
                            <span className="bold">Lineup</span>
                            {sortLineup(starters)?.map(lineup => renderLineup(lineup, false, true, false))}
                        </div>
                        <div className="sub-col">
                            <span className="bold">Bench</span>
                            {bench.map(lineup => renderLineup(lineup, true, false, true))}
                        </div>
                    </div>
                    {used.length > 0 && (
                        <div className="sub-used">
                            <span className="bold">Out of Game</span>
                            {used.map(lineup => renderLineup(lineup, false, false, true))}
                        </div>
                    )}
                    <div className="sub-confirm-bar">
                        <div className="sub-confirm-summary">
                            <span className="sub-confirm-slot sub-confirm-out">
                                OUT: {subOutPlayer ? `${subOutPlayer.Card.name} (${subOutPlayer.Card.rating})` : '—'}
                            </span>
                            <span className="sub-confirm-arrow">→</span>
                            <span className="sub-confirm-slot sub-confirm-in">
                                IN: {subInPlayer ? `${subInPlayer.Card.name} (${subInPlayer.Card.rating})` : '—'}
                            </span>
                        </div>
                        <div className="sub-confirm-actions">
                            <button
                                type="button"
                                className="btn-danger"
                                onClick={onClose}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={submitSubstitution}
                                disabled={submitting || !subInPlayer || !subOutPlayer}
                            >
                                Substitute
                            </button>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div className="lineup-part">
                        <span className="bold">Lineup</span>
                        {sortLineup(starters)?.map(lineup => renderLineup(lineup, false, true, false))}
                    </div>
                    {bench.length > 0 && (
                        <div className="lineup-part">
                            <span className="bold">Bench</span>
                            {bench.map(lineup => renderLineup(lineup, true, false, true))}
                            {used.length > 0 && (
                                <div className="lineup-part">
                                    <span className="bold">Out of Game</span>
                                    {used.map(lineup => renderLineup(lineup, false, false, true))}
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

GameLineup.propTypes = {
    participant: PropTypes.shape({
        gameID: PropTypes.any,
        gameParticipantID: PropTypes.any,
        role: PropTypes.string.isRequired,
        userID: PropTypes.any,
        UserTeam: PropTypes.shape({
            name: PropTypes.string,
        }),
        GameLineups: PropTypes.arrayOf(
            PropTypes.shape({
                gameLineupID: PropTypes.any,
                fieldingPosition: PropTypes.string,
                lineupOrder: PropTypes.number,
                Card: PropTypes.shape({
                    year: PropTypes.number,
                    name: PropTypes.string,
                    rating: PropTypes.number,
                    positions: PropTypes.string,
                }),
            })
        ).isRequired,
    }).isRequired,
    latestEvent: PropTypes.shape({
        nextBatterLineup: PropTypes.shape({
            gameLineupID: PropTypes.any,
        }),
        nextPitcherLineup: PropTypes.shape({
            gameLineupID: PropTypes.any,
        }),
        gameEventID: PropTypes.any,
    }),
    isSub: PropTypes.bool,
    onClose: PropTypes.func,
    pitcherWorkMap: PropTypes.instanceOf(Map),
};

export default GameLineup;
